import React from 'react';
// Deep import, NOT `from '@platform/tutorial-recorder'`. The package's
// index.ts barrel re-exports session.ts and api.ts alongside adminGate.ts,
// and this file is imported eagerly (App.tsx is not lazy). Importing the
// barrel here pulls the whole module graph -- including RecordingSession's
// `getDisplayMedia` call -- into the MAIN entry bundle even though nothing
// here references it, defeating RecorderOverlay's own lazy import.
// Reaching straight into adminGate.ts, whose only dependency is
// decodeJwtPayload, keeps this gate's footprint to itself. Verified by
// Step 5: `getDisplayMedia` must appear only in the RecorderOverlay chunk.
import { isTutorialAdmin } from '@platform/tutorial-recorder/src/adminGate';

// Lazy so the recorder (RecordingSession, the upload/transcribe pipeline,
// the whole picker UI) never ships to a merchant's bundle — only the tiny
// admin+capability gate below is in the main chunk. Verified by the bundle
// check in the task report: RecordingSession, submitRecording,
// createRecorderApi, presignClip, cleanNarration and buildSteps all have
// ZERO matches in the entry chunk. A plain `grep getDisplayMedia` is NOT
// that check — see the comment on canRecord() below for why.
const RecorderOverlay = React.lazy(() => import('./RecorderOverlay'));

/**
 * getDisplayMedia does not exist in the iOS WKWebView, so the Capacitor
 * build can never record. Feature-detect rather than offering a button that
 * fails on tap — recording happens in desktop Chrome only.
 */
// This probe MUST stay eager: it decides whether to lazy-import the overlay at
// all. That means the literal `getDisplayMedia` appears in the entry bundle —
// expected, and not the same thing as shipping the recorder. The property that
// matters is that RecordingSession and the upload pipeline are absent from the
// entry chunk; see the bundle check in the task report.
const canRecord = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getDisplayMedia === 'function';

/**
 * Whether the signed-in viewer should be offered "Record a tutorial" at all —
 * a super-admin claim AND a browser that can actually record. A merchant, and
 * any admin on Capacitor (no `getDisplayMedia` in WKWebView), gets neither the
 * menu item nor this mount. Exported so the help/support launcher
 * (`components/issueReporter/IssueReporterRoot.tsx`) can decide whether to
 * show the menu row without duplicating the gate.
 *
 * Ported verbatim from platform-app (2026-09-29, owner: "Record a tutorial"
 * in super admin too). A super admin has no active tenant, so recordings made
 * here are GLOBAL tutorials (no x-tenant-id — see tutorialHost).
 */
export function canShowTutorialRecorder(): boolean {
  try {
    // Session token key is `jwtToken` — even though the *value* stored
    // under it is a Cognito ID token. Do not "correct" this to `idToken`:
    // that key is only ever *removed* (see signupSession.ts's defensive
    // Cognito-storage wipe and SignupSuccess.tsx), never set with a real
    // session token, so gating on it would make this gate pass for
    // nobody, admins included.
    return isTutorialAdmin(localStorage.getItem('jwtToken')) && canRecord();
  } catch {
    // Private-mode / storage-disabled browsers throw on localStorage
    // access — treat as not eligible rather than crash the app shell.
    return false;
  }
}

interface Props {
  /** Whether the recorder PANEL is visible. This component itself stays
   * mounted once the viewer is gated in, regardless of this flag, so an
   * in-progress recording survives the panel being closed — only the panel
   * UI is toggled by the launcher's menu. */
  panelOpen: boolean;
  onClose: () => void;
  onRecordingChange?: (recording: boolean, stepCount: number) => void;
  /** Threaded straight through to RecorderOverlay -- see its own prop doc.
   * Set by the launcher when the Tutorials review page's "re-record" action
   * asks to reopen the recorder already pointed at a specific tutorial. */
  initialTutorialId?: string;
}

/**
 * Single mount point for the in-app tutorial recorder overlay. Renders
 * nothing unless `canShowTutorialRecorder()` passes.
 *
 * Mounted alongside `IssueReporterRoot`'s other floating pieces, outside the
 * router (App.tsx), so a recording survives navigation across the whole app
 * the same way issue reporting does.
 */
export default function TutorialRecorderMount({ panelOpen, onClose, onRecordingChange, initialTutorialId }: Props) {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    setShow(canShowTutorialRecorder());
  }, []);

  if (!show) return null;

  return (
    <React.Suspense fallback={null}>
      <RecorderOverlay
        panelOpen={panelOpen}
        onClose={onClose}
        onRecordingChange={onRecordingChange}
        initialTutorialId={initialTutorialId}
      />
    </React.Suspense>
  );
}
